import { Search } from 'lucide-react';
import { ElementRef, useEffect, useMemo, useRef, useState } from 'react';

import { AnimateSpin } from '@/components/AnimateSpin';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { useDebounce } from '@/hooks/useDebounce';
import { useGetPrompts } from '@/hooks/useGetPrompts';
import { Prompt } from '@/services/PromptsService';

interface PromptSelectProps {
  value?: string; // prompt id
  onChange: (value: string, prompt?: Prompt) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function PromptSelect({
  value,
  onChange,
  placeholder = 'Select prompt',
  disabled,
}: PromptSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  function getSearchFilters(value: string): {
    promptName?: string;
    serviceName?: string;
  } {
    const q = value.trim();
    if (!q) return {};
    const idx = q.indexOf(':');
    if (idx !== -1) {
      const left = q.slice(0, idx).trim();
      const right = q.slice(idx + 1).trim();
      return {
        promptName: left || undefined,
        serviceName: right || undefined,
      };
    }
    return { promptName: q };
  }

  const filters = getSearchFilters(debouncedSearch);

  const { prompts, isFetching, hasNextPage, fetchNextPage } = useGetPrompts({
    limit: 10,
    promptName: filters.promptName,
    serviceName: filters.serviceName,
  });

  // Infinite scroll within popover
  const endRef = useRef<ElementRef<'div'>>(null);
  useEffect(() => {
    if (!open || !endRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetching) {
        fetchNextPage();
      }
    });

    observer.observe(endRef.current);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetching, fetchNextPage]);

  const promptsById = useMemo(() => {
    const map = new Map<string, Prompt>();
    prompts.forEach((p) => {
      if (p.id !== undefined && p.id !== null) map.set(String(p.id), p);
    });
    return map;
  }, [prompts]);

  const selected = value ? promptsById.get(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selected
            ? `${selected.promptName}${selected.version ? ` (v${selected.version})` : ''} — ${selected.serviceName}`
            : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-2">
        <div className="mb-2 space-y-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search by promptName or promptName:serviceName"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10"
            />
          </div>
          <div className="px-1 text-xs text-muted-foreground">
            Tip: use promptName:serviceName. Example:
            <br />
            homepage_banner:TONE_OF_VOICE
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {prompts.length === 0 && !isFetching && (
            <div className="px-2 py-3 text-sm text-muted-foreground">
              No prompts found
            </div>
          )}

          {prompts.map((p) => (
            <button
              key={String(p.id)}
              type="button"
              className="block w-full cursor-pointer rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => {
                // Prevent focus loss closing popover before selection
                e.preventDefault();
              }}
              onClick={() => {
                if (p.id !== undefined && p.id !== null) {
                  onChange(String(p.id), p);
                  setOpen(false);
                }
              }}
            >
              <div className="font-medium">{p.promptName}</div>
              <div className="text-xs text-muted-foreground">
                {p.serviceName}
                {p.version ? ` • v${p.version}` : ''}
              </div>
            </button>
          ))}

          <div ref={endRef} className="flex items-center justify-center py-2">
            {isFetching && <AnimateSpin className="h-4 w-4" />}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
